import { constants } from '@eti/utils';
import { useTranslation } from '@eti/providers';
import { checkInBaggage, secondBaggagePerTraveler } from '@eti/products';
import type {
  AncillaryBundlesOnTdPageFragment,
  CheckedBaggageSummary,
  Maybe,
  Summary,
} from '@eti/schema-types';
import {
  INCLUDED_CHECKED_BAGGAGE,
  INFANT_NO_CHECKED_BAGGAGE,
  NO_CHECKED_BAGGAGE_AVAILABLE,
} from '../../../constants/includedBaggageConstants';
import {
  resolveBaggageForTraveler,
  resolveTravelerLabel,
  resolveSecondBaggageForTraveler,
} from '../utils/baggage';
import type {
  TransformedTDCartProductType,
  TransformedCartTravelerPriceType,
  TransformedSelectedBundleType,
} from '../../../common/types/transformations/models';

const { INFANT } = constants.ageTypes;

const checkSummariesFor = (status: string, summaries: Maybe<Array<Maybe<Summary>>> | null = []) =>
  summaries?.some((traveler) => traveler?.baggageStatus === status);

const useIncludedOrUnavailableCheckInBaggage = (
  travelerPrices: TransformedCartTravelerPriceType[] = [],
  checkedBaggageSummary: CheckedBaggageSummary = {},
  bundleData: AncillaryBundlesOnTdPageFragment | TransformedSelectedBundleType,
  checkInBaggageId: number,
  addedProducts: TransformedTDCartProductType[],
) => {
  const { t } = useTranslation();
  const { tripSummaries } = checkedBaggageSummary;
  const infantOnTrip = checkSummariesFor(INFANT_NO_CHECKED_BAGGAGE, tripSummaries);
  const bundleContents = bundleData?.options?.find(
    (option) => option?.id === bundleData?.selectedOption,
  );
  const bundleBaggageDetails =
    bundleContents?.content?.find(
      (bundleContent) =>
        bundleContent?.pieces !== null && Number(bundleContent?.productTypeId) === checkInBaggageId,
    ) || null;
  const isBaggageIncludedOnTrip =
    checkSummariesFor(INCLUDED_CHECKED_BAGGAGE, tripSummaries) || bundleBaggageDetails;
  const isBaggageUnavailableForTrip = checkSummariesFor(
    NO_CHECKED_BAGGAGE_AVAILABLE,
    tripSummaries,
  );

  if (isBaggageUnavailableForTrip) {
    return [{ label: t('Cart.Baggage.NotAvailable'), noBaggageAvailable: true }];
  }

  if (!isBaggageIncludedOnTrip) {
    return null;
  }

  const { baggageDetails } = tripSummaries?.find((summary) => summary?.baggageDetails) || {};

  const pieces = baggageDetails?.pieces || bundleBaggageDetails?.pieces;
  const weight = baggageDetails?.weight || bundleBaggageDetails?.weight;
  const weightUnit = baggageDetails?.weightUnit || bundleBaggageDetails?.weightUnit;

  const onlyCheckedBaggageIncluded = infantOnTrip
    ? travelerPrices.map((traveler) => ({
        label: resolveTravelerLabel(t, traveler),
        value:
          traveler.ageType === INFANT
            ? t('Cart.Baggage.ContactAirline')
            : resolveBaggageForTraveler(t, baggageDetails, bundleBaggageDetails),
        noBaggageAvailable: undefined,
      }))
    : [
        {
          label: pieces
            ? t('Cart.Baggage.IncludedForAll.Pieces', pieces, weight, weightUnit)
            : t('Cart.Baggage.IncludedForAll.Weight', weight, weightUnit),
          noBaggageAvailable: undefined,
        },
      ];

  const baggageSelection =
    addedProducts?.find(
      (product) =>
        product.id === checkInBaggage.id ||
        String(product.id) === String(secondBaggagePerTraveler.id),
    ) ?? null;

  return baggageSelection !== null
    ? travelerPrices.map((traveler) => {
        const baggageSelectionForTraveler = baggageSelection?.travelers.find(
          (selection) => selection.travelerId === traveler.travelerId,
        );
        if (baggageSelectionForTraveler) {
          if (
            baggageSelection.id === checkInBaggage.id ||
            String(baggageSelection.id) === String(secondBaggagePerTraveler.id)
          ) {
            return {
              label: resolveTravelerLabel(t, traveler),
              value:
                traveler.ageType === INFANT && infantOnTrip
                  ? t('Cart.Baggage.ContactAirline')
                  : resolveBaggageForTraveler(t, { pieces, weight, weightUnit }),
              secondBagValue: resolveSecondBaggageForTraveler(t, baggageSelectionForTraveler),
              noBaggageAvailable: undefined,
            };
          }
        }

        return {
          label: resolveTravelerLabel(t, traveler),
          value:
            traveler.ageType === INFANT && infantOnTrip
              ? t('Cart.Baggage.ContactAirline')
              : resolveBaggageForTraveler(t, { pieces, weight, weightUnit }),
          noBaggageAvailable: undefined,
        };
      })
    : onlyCheckedBaggageIncluded;
};

export default useIncludedOrUnavailableCheckInBaggage;
